import { Link, NavLink } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ShoppingBag, ChevronDown } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";

const NAV = [
  { to: "/", key: "inicio" },
  { to: "/productos", key: "productos" },
  { to: "/produccion", key: "produccion" },
  { to: "/logistica", key: "logistica" },
  { to: "/tienda", key: "tienda" },
  { to: "/eventos", key: "eventos" },
  { to: "/contacto", key: "contacto" },
];

// Flag emoji per language code
const FLAGS = {
  es: "🇪🇸",
  en: "🇬🇧",
  fr: "🇫🇷",
  de: "🇩🇪",
};
const LANG_NAMES = {
  es: "Español",
  en: "English",
  fr: "Français",
  de: "Deutsch",
};

const Header = () => {
  const { t, lang, setLang, langs } = useLang();
  const { count, setOpen: setCartOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close lang dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-bg/95 backdrop-blur-md border-b border-bone/60 shadow-sm"
          : "bg-bg/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-4 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group flex-shrink-0"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src="/logo-criollita.png"
              alt="La Criollita — Productos Línea Arte"
              className="h-14 md:h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.key}`}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-300 link-underline ${
                    isActive ? "text-wine" : "text-ink hover:text-wine"
                  }`
                }
              >
                {t.nav[item.key]}
              </NavLink>
            ))}
          </nav>

          {/* Right: lang dropdown + cart + mobile toggle */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Language dropdown with flags */}
            <div ref={langRef} className="relative">
              <button
                data-testid="lang-dropdown-toggle"
                onClick={() => setLangOpen((s) => !s)}
                className="flex items-center gap-2 px-3 py-2 border border-bone hover:border-ink transition-colors group"
                aria-haspopup="true"
                aria-expanded={langOpen}
              >
                <span className="text-lg leading-none" aria-hidden="true">{FLAGS[lang]}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] font-medium text-ink hidden sm:inline">
                  {lang}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-ink-muted transition-transform ${
                    langOpen ? "rotate-180" : ""
                  }`}
                  strokeWidth={1.5}
                />
              </button>

              {langOpen && (
                <ul
                  data-testid="lang-dropdown-menu"
                  className="absolute right-0 top-full mt-2 min-w-[180px] bg-bg border border-bone shadow-lg z-50 animate-fade-up"
                  role="menu"
                >
                  {langs.map((l) => (
                    <li key={l.code}>
                      <button
                        data-testid={`lang-${l.code}`}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors border-l-2 ${
                          lang === l.code
                            ? "bg-surface text-wine border-wine"
                            : "border-transparent text-ink hover:bg-surface hover:border-bone"
                        }`}
                      >
                        <span className="text-lg leading-none" aria-hidden="true">
                          {FLAGS[l.code]}
                        </span>
                        <span className="flex-1 font-light">{LANG_NAMES[l.code]}</span>
                        <span className="text-[10px] uppercase tracking-widest text-ink-muted">
                          {l.label}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Cart */}
            <button
              data-testid="cart-toggle-header"
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-2 text-ink hover:text-wine transition-colors p-1"
              aria-label={t.nav.cart}
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 bg-wine text-bg text-[10px] w-5 h-5 flex items-center justify-center font-medium"
                >
                  {count}
                </span>
              )}
            </button>

            {/* Mobile toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((s) => !s)}
              className="lg:hidden text-ink p-1"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-bg border-t border-bone"
        >
          <div className="px-6 py-8 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.key}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `py-3 font-serif text-3xl tracking-tight transition-colors ${
                    isActive ? "text-wine italic" : "text-ink"
                  }`
                }
              >
                {t.nav[item.key]}
              </NavLink>
            ))}
            <div className="mt-6 pt-6 border-t border-bone">
              <div className="text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-3 font-medium">
                Idioma · Language
              </div>
              <div className="flex flex-wrap gap-2">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`flex items-center gap-2 px-3 py-2 border text-sm transition-colors ${
                      lang === l.code
                        ? "bg-wine text-bg border-wine"
                        : "border-bone text-ink hover:border-ink"
                    }`}
                  >
                    <span className="text-base leading-none" aria-hidden="true">
                      {FLAGS[l.code]}
                    </span>
                    <span className="text-[11px] uppercase tracking-widest font-medium">
                      {l.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
